/**
 * Company settings that live in sv_control: the desktop API bridge and its tokens, the
 * registered bridges, and the security hint. All tenant-scoped, so `requestControl()`.
 *
 * The notification switches are their own entity (`entities/notification-settings`) —
 * same `/control/settings/*` prefix, different concern, and it predates this file.
 */
import {requestControl} from '@/shared/api';
import type {
  ApiBridgeSettings,
  DesktopBridge,
  DesktopList,
  MintedApiToken,
  SecuritySettings,
} from './types';

export function fetchApiBridgeSettings(
  companyId: string,
): Promise<ApiBridgeSettings> {
  return requestControl<ApiBridgeSettings>('/control/settings/api', companyId);
}

/**
 * Mint a token for a desktop bridge.
 *
 * The response is the ONLY time the secret exists in a readable form — the list route
 * returns a prefix and nothing more. A caller that does not put it in front of the user
 * immediately has silently thrown it away, and the only recovery is minting another.
 */
export function createApiToken(
  companyId: string,
  name: string,
): Promise<MintedApiToken> {
  return requestControl<MintedApiToken>('/control/settings/api/tokens', companyId, {
    method: 'POST',
    body: JSON.stringify({name}),
  });
}

/**
 * Revoke a token. Anything still using it — a desktop bridge, a terminal that was
 * issued one at adoption — stops syncing at its next call, so the list has to make
 * clear which tokens belong to devices before offering this.
 */
export function revokeApiToken(
  companyId: string,
  tokenId: string,
): Promise<void> {
  return requestControl<void>(
    `/control/settings/api/tokens/${encodeURIComponent(tokenId)}`,
    companyId,
    {method: 'DELETE'},
  );
}

export function fetchSecuritySettings(
  companyId: string,
): Promise<SecuritySettings> {
  return requestControl<SecuritySettings>('/control/settings/security', companyId);
}

/**
 * GET /control/desktops — the desktop bridges registered against this company.
 *
 * ## Three shapes, all accepted
 *
 * This is a `/control/*` LIST, and the envelope on those is not settled by reading one
 * source. sv_control names its own array `desktops`; the gateway re-wraps list
 * responses in its standard `{meta, data}` — that difference already cost this app a
 * silently empty profiles screen (see `entities/device/api.ts`, fetchProfiles) — and
 * the 2026-08-24 changelog verifies this very route with `jq '.[0]'`, i.e. a bare
 * array. Reading one key and hoping is how a company with desktops registered gets a
 * screen saying it has none, which looks like a fact rather than a bug.
 *
 * ## Casing
 *
 * Same standoff on the fields. The live operation description lists them snake_case
 * (`install_id`, `last_seen_at`, plus `unlinked_at`/`unlinked_by`), the changelog
 * writes the JSON camelCase, and there is no published schema to break the tie. Read
 * either; the cost is a few lines against a screen that shows every machine as a blank
 * row and every departure as nothing at all.
 */
type DesktopWire = Record<string, unknown>;

function desktopStr(row: DesktopWire, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'string' && v.length > 0) {
      return v;
    }
  }
  return '';
}

function toDesktop(row: DesktopWire): DesktopBridge {
  return {
    install_id: desktopStr(row, 'install_id', 'installId'),
    name: desktopStr(row, 'name'),
    hostname: desktopStr(row, 'hostname'),
    os: desktopStr(row, 'os'),
    os_version: desktopStr(row, 'os_version', 'osVersion'),
    app_version: desktopStr(row, 'app_version', 'appVersion'),
    mode: desktopStr(row, 'mode'),
    online: row.online === true,
    registered_at: desktopStr(row, 'registered_at', 'registeredAt'),
    last_seen_at: desktopStr(row, 'last_seen_at', 'lastSeenAt') || null,
    unlinked_at: desktopStr(row, 'unlinked_at', 'unlinkedAt') || null,
    unlinked_by: desktopStr(row, 'unlinked_by', 'unlinkedBy') || null,
  };
}

export async function fetchDesktops(companyId: string): Promise<DesktopList> {
  const res = await requestControl<
    DesktopWire[] | {data?: DesktopWire[]; desktops?: DesktopWire[]}
  >('/control/desktops', companyId);
  const rows = Array.isArray(res) ? res : (res?.data ?? res?.desktops ?? []);
  return {desktops: rows.map(toDesktop)};
}
